import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  groupByPermissionGroup,
  usePermissionList,
  type Permission,
} from "@/features/permission";
import {
  useRolePermissions,
  useSetSystemRolePermissions,
} from "../hooks/useSystemRoles";

interface RolePermissionsPanelProps {
  roleGuid: string;
}

export function RolePermissionsPanel({ roleGuid }: RolePermissionsPanelProps) {
  const { data: catalog, isLoading: catalogLoading } = usePermissionList();
  const { data: current, isLoading: currentLoading } =
    useRolePermissions(roleGuid);
  const setPermissions = useSetSystemRolePermissions();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [seenCurrent, setSeenCurrent] = useState<typeof current>(undefined);

  // Sync local selection when server data first arrives or changes.
  // Setting state during render is the recommended React pattern for this case.
  if (current !== seenCurrent) {
    setSeenCurrent(current);
    setSelected(new Set((current ?? []).map((p) => p.permissionGuid)));
  }
  const initialized = seenCurrent !== undefined;

  const grouped = useMemo(() => groupByPermissionGroup(catalog), [catalog]);

  const togglePermission = (guid: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(guid)) next.delete(guid);
      else next.add(guid);
      return next;
    });
  };

  const toggleGroup = (groupItems: Permission[], allChecked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const p of groupItems) {
        if (allChecked) next.delete(p.guid);
        else next.add(p.guid);
      }
      return next;
    });
  };

  const handleReset = () => {
    if (!current) return;
    setSelected(new Set(current.map((p) => p.permissionGuid)));
  };

  const handleSave = () => {
    setPermissions.mutate({
      roleGuid,
      permissionGuids: Array.from(selected),
    });
  };

  const isLoading = catalogLoading || currentLoading;
  const hasChanges =
    initialized &&
    !!current &&
    (selected.size !== current.length ||
      current.some((p) => !selected.has(p.permissionGuid)));

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center text-xs text-gray-400">
        로딩 중...
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-2">
        <p className="text-xs text-gray-600">
          체크된 권한이 이 역할에 부여됩니다. 저장 시 모든 회사 복제본에 즉시 동기화됩니다.
        </p>
        <span className="text-xs font-medium text-gray-700">
          {selected.size}개 선택
        </span>
      </div>

      <div className="flex-1 overflow-y-auto">
        {grouped.length === 0 ? (
          <div className="flex h-32 items-center justify-center text-xs text-gray-400">
            등록된 권한이 없습니다
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {grouped.map(({ group, items }) => {
              const checkedCount = items.filter((p) =>
                selected.has(p.guid),
              ).length;
              const allChecked = checkedCount === items.length;
              const someChecked = checkedCount > 0 && !allChecked;
              return (
                <section key={group}>
                  <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-1.5">
                    <label className="flex cursor-pointer items-center gap-2">
                      <Checkbox
                        checked={allChecked}
                        indeterminate={someChecked}
                        onCheckedChange={() => toggleGroup(items, allChecked)}
                      />
                      <span className="text-xs font-medium uppercase text-gray-700">
                        {group}
                      </span>
                      <span className="text-xs text-gray-400">
                        {checkedCount}/{items.length}
                      </span>
                    </label>
                  </div>
                  <ul>
                    {items.map((p) => {
                      const checked = selected.has(p.guid);
                      return (
                        <li
                          key={p.guid}
                          className="border-b border-gray-100 hover:bg-gray-50"
                        >
                          <label className="flex cursor-pointer items-start gap-2 px-4 py-2">
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => togglePermission(p.guid)}
                              className="mt-0.5"
                            />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-xs text-gray-900">
                                  {p.code}
                                </span>
                                <span className="text-xs text-gray-700">
                                  {p.label}
                                </span>
                              </div>
                              {p.description && (
                                <p className="mt-0.5 text-xs text-gray-500">
                                  {p.description}
                                </p>
                              )}
                            </div>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-4 py-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!hasChanges || setPermissions.isPending}
          onClick={handleReset}
          className="text-xs"
        >
          되돌리기
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={!hasChanges || setPermissions.isPending}
          onClick={handleSave}
          className="bg-blue-600 px-4 text-xs font-bold hover:bg-blue-700"
        >
          {setPermissions.isPending ? "저장 중..." : "저장"}
        </Button>
      </div>
    </div>
  );
}
